import { selectStubUrl } from '../../selectors/app';
import { selectUser } from '../../selectors/auth';
import { selectTeamsContext } from '../../selectors/conversation';
import { showNotification } from '../app/actionCreators';
import {
    appConstants,
    AppErrorCode,
    NotificationStatus,
} from '../app/actionTypes';
import { MappingType, nextcloudApi } from 'nextcloud-api';
import { ThunkAction } from 'redux-thunk';
import { selectTranslate } from '../../selectors/ui';
import { AnyAction } from 'redux';
import { RootState } from '../..';
import {
    setDefaultFolder,
    setFormDefaultFolderId,
    setFormDefaultFolderPath,
    setFormDefaultFolderScope,
    setFormDefaultFolderCircles,
    setDefaultFolderModalStep,
} from './actionCreators';
import {
    selectServerFiles,
    selectStubUrl as selectAppStubUrl,
} from '../../selectors/app';
import { showNotification as showAppNotification } from '../app/actionCreators';
import {
    AppErrorCode as AppErr,
    NotificationStatus as Notif,
} from '../app/actionTypes';
import {
    selectDefaultFolderScope,
    selectFormDefaultFolderCircle,
    selectFormDefaultFolderId,
    selectFormDefaultFolderScope,
} from '../../selectors/defaultFolder';
import { shareSelectedFolderWithCircleThunk } from '../nextcloud/thunks';
import { logger } from '../../../helpers/logger';

export const setDefaultFolderThunk = (): ThunkAction<
    Promise<void>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const stubUrl = selectStubUrl(state);
        const user = selectUser(state);
        const teamsContext = selectTeamsContext(state);
        const translate = selectTranslate(state);
        if (!stubUrl || !user || !teamsContext) return;

        try {
            let mapping = await nextcloudApi.getFolderMappingMetadata(
                stubUrl,
                teamsContext.channel.id,
                user,
            );

            if (!mapping.success || !mapping.result) {
                mapping = await nextcloudApi.getFolderMappingMetadata(
                    stubUrl,
                    teamsContext.team.groupId,
                    user,
                );
            }

            if (!mapping.success || !mapping.result?.folder_id) {
                dispatch(
                    setDefaultFolder({
                        id: null,
                        path: null,
                        scope: null,
                        circle: null,
                    }),
                );
                return;
            }

            const scope = mapping.result.type;
            const folderId = String(mapping.result.folder_id);
            const circle = String(mapping.result.nextcloud_team_id);

            const searchRes = await nextcloudApi.searchFolderMetadata(
                stubUrl,
                user,
                folderId,
            );

            if (!searchRes.success || !searchRes.result) {
                dispatch(
                    showNotification({
                        message: translate('something_went_wrong'),
                        status: NotificationStatus.Error,
                        errorCode: AppErrorCode.CustomError,
                    }),
                );
                return;
            }

            const xml = String(searchRes.result);
            const doc = new DOMParser().parseFromString(xml, 'text/xml');
            const hrefEl = doc
                .getElementsByTagName('d:multistatus')[0]
                ?.getElementsByTagName('d:response')[0]
                ?.getElementsByTagName('d:href')[0];
            const href = hrefEl?.textContent ?? '';
            if (!href) {
                dispatch(
                    showNotification({
                        message: translate('something_went_wrong'),
                        status: NotificationStatus.Error,
                        errorCode: AppErrorCode.CustomError,
                    }),
                );
                return;
            }

            const decoded = decodeURIComponent(href);
            const prefix = `/remote.php/dav/files/${user.loginName}`;
            const relative = decoded.startsWith(prefix)
                ? decoded.slice(prefix.length)
                : decoded;
            const resolvedPath = relative.replace(/\/$/, '');

            dispatch(
                setDefaultFolder({
                    id: folderId,
                    path: resolvedPath,
                    scope: scope,
                    circle: circle,
                }),
            );
            dispatch({
                type: appConstants.APP_DESTINATION_FOLDER,
                payload: { destinationFolderPath: resolvedPath },
            });
        } catch (err) {
            logger.error(
                `setDefaultFolderThunk error: ${
                    err instanceof Error ? err.message : 'unknown'
                }`,
                'Files',
            );
        }
    };
};

export const setMappingThunk = (
    folderId: number,
    circleId: string,
    scope: MappingType,
    overwrite: boolean = false,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const teamContext = selectTeamsContext(state);
        const stubUrl = selectAppStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !teamContext || !stubUrl) {
            return;
        }

        const msId =
            scope === MappingType.Channel
                ? teamContext.channel.id
                : teamContext.team.groupId;

        try {
            const existing = await nextcloudApi.getFolderMappingMetadata(
                stubUrl,
                msId,
                user,
            );

            if (existing.success && existing.result) {
                if (!overwrite) {
                    return;
                }
                await nextcloudApi.removeMapping(stubUrl, msId, user);
            }

            const setRes = await nextcloudApi.setFolderMappingMetadata(
                stubUrl,
                msId,
                folderId.toString(),
                circleId,
                user,
                scope,
            );

            if (setRes.success) {
                const serverFiles = selectServerFiles(state);
                const selectedFolder = serverFiles?.find(
                    (file) => file.fileId === folderId,
                );
                if (selectedFolder) {
                    dispatch(
                        setDefaultFolder({
                            id: selectedFolder.fileId.toString(),
                            path: selectedFolder.path,
                            circle: circleId,
                            scope,
                        }),
                    );
                    dispatch(
                        showAppNotification({
                            message: translate(
                                'default_folder_set_successfully',
                            ),
                            status: Notif.Success,
                        }),
                    );
                }
            } else {
                dispatch(
                    showAppNotification({
                        message: translate('failed_to_set_default_folder'),
                        status: Notif.Error,
                        errorCode: AppErr.CustomError,
                    }),
                );
            }
        } catch (error) {
            dispatch(
                showAppNotification({
                    message: translate('error_setting_default_folder'),
                    status: Notif.Error,
                    errorCode: AppErr.CustomError,
                }),
            );
        }
    };
};

export const removeDefaultFolderThunk = (): ThunkAction<
    void,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const teamsContext = selectTeamsContext(state);
        const stubUrl = selectAppStubUrl(state);
        const defaultScope = selectDefaultFolderScope(state);
        const translate = selectTranslate(state);

        if (!user || !teamsContext || !stubUrl) {
            return;
        }

        const msId =
            defaultScope === MappingType.Channel
                ? teamsContext.channel.id
                : teamsContext.team.groupId;

        await nextcloudApi.removeMapping(stubUrl, msId, user);

        dispatch(
            setDefaultFolder({
                id: null,
                path: null,
                scope: null,
                circle: null,
            }),
        );
        dispatch(
            showAppNotification({
                message: translate('default_folder_removed_successfully'),
                status: Notif.Success,
            }),
        );
    };
};

export const clearDefaultFolderFormData = (): ThunkAction<
    void,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch): Promise<void> {
        dispatch(setFormDefaultFolderId(null));
        dispatch(setFormDefaultFolderPath(null));
        dispatch(setFormDefaultFolderScope(null));
        dispatch(setFormDefaultFolderCircles(null));
    };
};

export const sendDefaultFolderFormThunk = (): ThunkAction<
    Promise<void>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const selectedFolderId = selectFormDefaultFolderId(state);
        const selectedScope = selectFormDefaultFolderScope(state);
        const selectedCircle = selectFormDefaultFolderCircle(state);

        dispatch(setDefaultFolderModalStep(null));
        await dispatch(shareSelectedFolderWithCircleThunk());
        dispatch(
            setMappingThunk(
                selectedFolderId,
                selectedCircle.id,
                selectedScope,
                true,
            ),
        );
        dispatch(clearDefaultFolderFormData());
    };
};
